import { BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM, BRANCH_ENVIRONMENT_PRODUCTION_ENUM } from '@takeshape/branches';
import { type MergedBranchResponse, mergeBranchMutation } from 'app/branches/api';
import { createRoutine } from 'app/common/utils/new-routines';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import { diff } from 'deep-diff';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { compatSchemaToSchema } from '../../compat/utils.js';
import { showMessage } from '../../messages/actions.js';
import { fetchSchemaRoutine, setCurrentBranchCanPromote, setCurrentBranchNeedsUpdate } from '../actions.js';
import { DEFAULT_BRANCH } from '../constants.js';
import { getCurrentBranch, getCurrentBranchName, getDefaultUserSchema } from '../selectors.js';
import type { Branch } from '../types.js';
import { waitForFetchSchema } from './fetch-schema.js';

export const checkBranchStatus = createRoutine('branches/CHECK_BRANCH_STATUS')<
  undefined,
  undefined,
  undefined,
  null | { update: MergedBranchResponse; promote: MergedBranchResponse },
  string,
  undefined
>();

export function* checkBranchStatusSaga(): SagaIterator {
  const branchName = yield select(getCurrentBranchName);

  if (branchName === DEFAULT_BRANCH) {
    yield put(setCurrentBranchNeedsUpdate(false));
    yield put(setCurrentBranchCanPromote(false));
    return null;
  }

  yield put(setCurrentBranchNeedsUpdate(null));

  const meta = { branch: DEFAULT_BRANCH, runtime: false };

  yield put(fetchSchemaRoutine.requested(undefined, meta));

  const rejected = yield call(waitForFetchSchema, meta);
  if (rejected) {
    yield put(showMessage('error', 'Failed to check fetch schema'));
    return;
  }

  const mergeInput = {
    base: {
      environment: BRANCH_ENVIRONMENT_PRODUCTION_ENUM
    },
    head: {
      environment: BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM,
      branchName
    },
    dryRun: true
  };

  const [update, promote] = yield all([
    call(mergeBranchMutation, {
      ...mergeInput,
      target: {
        environment: BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM,
        branchName,
        version: 1
      }
    }),
    call(mergeBranchMutation, {
      ...mergeInput,
      target: {
        environment: BRANCH_ENVIRONMENT_PRODUCTION_ENUM,
        version: 1
      }
    })
  ]);

  return { update, promote };
}

export function* checkBranchStatusFulfilledSaga({
  payload
}: ActionType<typeof checkBranchStatus.fulfilled>): SagaIterator {
  if (!payload) {
    return;
  }

  const { update, promote } = payload;
  const updateMergedSchema = compatSchemaToSchema(update.mergedBranch.latestVersion.schema);
  const promoteMergedSchema = compatSchemaToSchema(promote.mergedBranch.latestVersion.schema);

  const currentBranch: Branch = yield select(getCurrentBranch);
  const currentSchemaCompat = currentBranch.layers.user.data;

  if (!currentSchemaCompat) {
    throw new Error('current branch user layer has no data');
  }

  const currentSchema = compatSchemaToSchema(currentSchemaCompat);

  const productionSchemaCompat = yield select(getDefaultUserSchema);
  const productionSchema = compatSchemaToSchema(productionSchemaCompat);

  const updateDiffResult = diff(updateMergedSchema, currentSchema);
  const promoteDiffResult = diff(promoteMergedSchema, productionSchema);

  yield put(setCurrentBranchNeedsUpdate(updateDiffResult !== undefined && updateDiffResult.length > 0));
  yield put(setCurrentBranchCanPromote(promoteDiffResult !== undefined && promoteDiffResult.length > 0));
}

export function* checkBranchStatusRejectedSaga(): SagaIterator {
  yield put(setCurrentBranchNeedsUpdate(false));
  yield put(setCurrentBranchCanPromote(false));
  yield put(showMessage('error', 'Failed to check branch status.'));
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(checkBranchStatus.requested, getRoutineSaga(checkBranchStatus, checkBranchStatusSaga)),
    takeLatest(checkBranchStatus.fulfilled, checkBranchStatusFulfilledSaga),
    takeLatest(checkBranchStatus.rejected, checkBranchStatusRejectedSaga)
  ]);
}
